import {useCallback, useMemo} from 'react';
import {isEmpty} from 'lodash';
import {useShallow} from 'zustand/react/shallow';
import {useDocumentContext} from '@/components/document/context';
import {useDocumentStore} from '@/components/document/store';
import FieldWrapper from '@/components/form/field-wrapper';
import FormControl from '@/components/form/form-control';
import {DocumentInputField, DocumentValue} from '@/engine/types';
import {FIELD_TYPES, LocalizedString} from '@/model/types';
import {useDocumentLocale, useSetFieldInitialValue, useDocumentMountEvent} from './hooks';
import {useFieldValue} from './hooks/use-field-value';
import {MetaField} from './meta-field';

type FormFieldProps = {
  field: DocumentInputField;
  sectionId: string;
};

export const FormField = ({field, sectionId}: FormFieldProps) => {
  const {documentModel, valueResolver, visibilitySet} = useDocumentContext();
  const {t, _t, locale} = useDocumentLocale();
  const storedValue = useDocumentStore(state => state.data[field.id]);
  const validation = useDocumentStore(state => state.validity[sectionId]?.fields?.[field.id]);
  const sectionValidation = useDocumentStore(state => state.validity[sectionId]);
  const {updateField, validateField, touchField, registerAttachment, unregisterAttachment} = useDocumentStore(
    useShallow(({updateField, unsetField, validateField, touchField, registerAttachment, unregisterAttachment}) => ({
      updateField,
      unsetField,
      validateField,
      touchField,
      registerAttachment,
      unregisterAttachment,
    })),
  );

  useDocumentMountEvent(field?.onMount);
  useSetFieldInitialValue(field, storedValue);
  useFieldValue(field);

  const cleanInputValue = (rawValue: DocumentValue | DocumentValue[]) => {
    if (typeof rawValue !== 'string') {
      return rawValue;
    }

    return rawValue.replace(/\t/g, '').trimStart();
  };

  const handleChange = useCallback(
    (rawValue: DocumentValue | DocumentValue[], validate = false) => {
      const value = cleanInputValue(rawValue);
      updateField(field.id, value);

      if (field.type === FIELD_TYPES.FILE) {
        value ? registerAttachment(sectionId, field.id, String(value)) : unregisterAttachment(sectionId, field.id);
      }

      field.onChange.forEach(action => action(value));

      if (validate) {
        validateField(sectionId, field.id, {documentModel, valueResolver});
      }
    },
    [
      documentModel,
      field.id,
      field.type,
      field.onChange,
      sectionId,
      updateField,
      validateField,
      valueResolver,
      registerAttachment,
      unregisterAttachment,
    ],
  );

  const handleBlur = useCallback(() => {
    field.onBlur.forEach(action => action());
    touchField(sectionId, field.id, {documentModel});
    validateField(sectionId, field.id, {documentModel, valueResolver});
  }, [field.onBlur, field.id, sectionId, documentModel, valueResolver, validateField, touchField]);

  const handleFocus = useCallback(() => {
    field.onFocus.forEach(action => action());
  }, [field.onFocus]);

  const handleClick = () => {
    field.onClick.forEach(action => action());
  };

  const value = valueResolver(storedValue, {visibilitySet});

  const relatedSectionErrors = useMemo(() => {
    if (!sectionValidation || sectionValidation.valid) return [];

    const errors = [];
    for (const error of sectionValidation.errors ?? []) {
      const fields = ((error.extra?.errorFields || error.extra?.fields) as string[]) ?? [];
      if (fields.includes(field.id)) {
        errors.push(error);
      }

      for (const fieldPattern of fields) {
        if (fieldPattern.endsWith('#*')) {
          const arrayFieldPrefix = fieldPattern.slice(0, -1);

          if (field.id.startsWith(arrayFieldPrefix)) {
            errors.push(error);
          }
        }
      }
    }

    return errors;
  }, [sectionValidation, field.id]);

  const isInError = useMemo(
    () => (validation && !validation.valid) || !isEmpty(relatedSectionErrors),
    [validation, relatedSectionErrors],
  );

  const errorMessages = useMemo(() => {
    const fieldErrors = (validation && !validation.valid ? validation.errors : []).filter(error => {
      return error.extra?.section === undefined;
    });

    if (fieldErrors.length === 0 && relatedSectionErrors.length === 0) return;

    const isCustomErrorMessage = (error: unknown): error is LocalizedString => {
      return !!error && typeof error === 'object' && 'en' in error && 'fr' in error;
    };

    return fieldErrors.concat(relatedSectionErrors).map(error => {
      if (isCustomErrorMessage(error.extra?.error) && error.extra.error[locale]) {
        return error.extra.error[locale];
      }

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      return t(`document.errors.${error.error as any}`, error.extra as any);
    });
  }, [validation, relatedSectionErrors, locale, t]);

  const hasLabel = !(field.type === FIELD_TYPES.CHECKBOX && !field.options?.length);

  return (
    <FormControl isRequired={field.required} isDisabled={field.disabled} isInError={isInError} errors={errorMessages}>
      <FieldWrapper
        label={hasLabel ? valueResolver(field.label, {visibilitySet}) : ''}
        identifier={field.id}
        helpDialogTitle={valueResolver(field.label, {visibilitySet})}
        helpDialogToggleAccessibilityLabel={t('components.field-wrapper.help-button-label-accessibility')}
        helpDialogContent={_t(valueResolver(field.helper))}
        useFieldset={['checkbox', 'radio'].includes(field.type)}
      >
        <MetaField
          field={field}
          value={value}
          storedValue={storedValue}
          handleChange={handleChange}
          handleBlur={handleBlur}
          handleFocus={handleFocus}
          handleClick={handleClick}
        />
      </FieldWrapper>
    </FormControl>
  );
};
